import React, { useState, useMemo, useCallback } from 'react';
import NotificationBell from '../notifications/NotificationBell';
import ChatWidget from '../chat/ChatWidget';
import { useChat } from '../../contexts/ChatContext';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Drawer, AppBar, Toolbar, List, Typography, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar, Collapse, ListSubheader, Tooltip, Badge } from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon, 
  People as UsersIcon, 
  AdminPanelSettings as RolesIcon, 
  Business as CompanyIcon, 
  Inventory as InventoryIcon, 
  Category as CategoryIcon, 
  Straighten as UnitIcon, 
  LocalShipping as SupplierIcon, 
  PersonOutline as CustomersIcon, 
  Store as BranchIcon, 
  Warehouse as WarehouseIcon, 
  ShoppingCart as PurchaseIcon, 
  Receipt as ReceiptIcon, 
  MoveToInbox as StockMoveIcon, 
  CompareArrows as TransferIcon, 
  PlaylistAddCheck as StockCountIcon, 
  AccountBalanceWallet as CustodyIcon, 
  AccountBalance as AccountingIcon, 
  Payment as PaymentIcon, 
  Assessment as ReportsIcon, 
  Settings as SettingsIcon, 
  Logout as LogoutIcon, 
  Language, 
  Brightness4, 
  Brightness7, 
  ChevronLeft, 
  ChevronRight, 
  ExpandLess, 
  ExpandMore, 
  AccountTree as AccountTreeIcon, 
  CalendarMonth as CalendarIcon, 
  Description as EntryTypeIcon, 
  ImportExport as ImportExportIcon, 
  TrendingDown, 
  Insights, 
  Schedule, 
  FastForward, 
  PlaylistAdd as OpeningIcon,
  PointOfSale as VoucherIcon,
  BarChart as FinancialReportIcon,
  TableChart as LedgerIcon,
  Badge as EmployeeIcon,
  Summarize as TrialBalanceIcon,
  AccessTime as TimeSheetIcon,
  CalendarToday as AttendanceIcon,
  AccountBalanceWallet as PayrollIcon,
  Report as ReportDocIcon,
  Warning as WarningIcon,
  MoneyOff as DeductionIcon,
  EventBusy as AbsenceIcon,
  FlightTakeoff as LeaveIcon,
  BarChart,
  Storefront as ConceptIcon,
  CreditCard as PaymentMethodIcon,
  Settings as ConfigIcon,
  ShoppingBasket as DailySalesIcon,
  BugReport as TestIcon,
  Code as DevIcon,
  AssignmentTurnedIn as BatchPostIcon,
  LocalShipping as ShippingIcon,
 PriceChange as PriceIcon,
  Description as ArchiveIcon,
  AttachFile as AttachIcon,
  LocalPizza as BakeryIssueIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux.hooks';
import { logout } from '../../store/slices/authSlice';
import authService from '../../services/auth.service';
import { setLoggingOut } from '../../services/axiosInstance';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import FiscalYearSelector from '../common/FiscalYearSelector';

const translations = {
  en: {
    systemName: 'Countora',
    // Dashboard
    dashboard: 'Dashboard',
    // Administration
    administration: 'Administration',
    users: 'Users',
    roles: 'Roles & Permissions',
    company: 'Company Settings',
    // Master Data
    masterData: 'Master Data',
    items: 'Items',
    categories: 'Categories',
    units: 'Units of Measure',
    branches: 'Branches',
    warehouses: 'Warehouses',
    // Partners
    partners: 'Partners',
    suppliers: 'Suppliers',
    customers: 'Customers',
    // HR & Custody
    hrCustody: 'HR & Custody',
    employees: 'Employees',
    custodies: 'Custodies',
    employeeCustodySummary: 'Employee Custody Summary',
    custodyExpenseCategories: 'Expense Categories',
    custodySettlements: 'Custody Settlements',
    // HR & Payroll
    hrPayroll: 'HR & Payroll',
    timeSheet: 'Time Sheet',
    timeSheetReport: 'Time Sheet Report',
    dailyAttendance: 'Daily Attendance',
    leaves: 'Leaves',
    payrolls: 'Payrolls',
    payrollReport: 'Payroll Report',
    hrAudit: 'HR Audit',
    absences: 'Absences',
    deductions: 'Deductions',
    warnings: 'Warnings',
    // Purchases
    purchases: 'Purchases',
    purchaseOrders: 'Purchase Orders',
    purchaseInvoices: 'Purchase Invoices',
    purchaseReturns: 'Purchase Returns',
    purchaseConsolidation: 'Orders Consolidation',
    bulkPricing: 'Bulk Pricing',
    bakeryPurchases: 'Bakery Purchases',
    // Stock Operations
    stockOperations: 'Stock Operations',
    inventoryOpeningBalances: 'Opening Balances',
    stockIssues: 'Stock Issues',
    warehouseTransfers: 'Warehouse Transfers',
    branchTransfers: 'Branch Transfers',
    stockCounts: 'Stock Counting',
    branchIssueRequests: 'Bakery Issue Requests',
    branchIssues: 'Bakery Issues to Branches',
    stockLevels: 'Stock Levels',
    stockMovements: 'Stock Movements',
    // Accounting
    accounting: 'Accounting',
    chartOfAccounts: 'Chart of Accounts',
    costCenters: 'Cost Centers',
    entryTypes: 'Entry Types',
    fiscalPeriods: 'Fiscal Periods',
    openingEntries: 'Opening Entries',
    journalEntries: 'Journal Entries',
    batchPosting: 'Batch Posting',
    // Vouchers
    vouchers: 'Vouchers',
    cashReceipts: 'Cash Receipts',
    cashPayments: 'Cash Payments',
    checkReceipts: 'Check Receipts',
    checkPayments: 'Check Payments',
    // Daily Sales System
    dailySalesSystem: 'Daily Sales',
    concepts: 'Concepts',
    paymentMethods: 'Payment Methods',
    accountConfigurations: 'Account Config',
    dailySales: 'Daily Sales',
    // Inventory Reports
    inventoryReports: 'Inventory Reports',
    stockLevelsReport: 'Stock Levels',
    stockMovementsReport: 'Stock Movements',
    stockIssuesReport: 'Stock Issues',
    warehouseTransfersReport: 'Warehouse Transfers',
    branchTransfersReport: 'Branch Transfers',
    stockCountsReport: 'Stock Counts',
    expiryReport: 'Expiry Report',
    fastMovingReport: 'Fast Moving Items',
    slowMovingReport: 'Slow Moving Items',
    inventoryValuationReport: 'Inventory Valuation',
    itemMovementStatementReport: 'Item Movement',
    // Purchase Reports
    purchaseReports: 'Purchase Reports',
    purchaseOrdersReport: 'Purchase Orders',
    purchaseInvoicesReport: 'Purchase Invoices',
    purchaseReturnsReport: 'Purchase Returns',
    consolidationReport: 'Consolidation',
    // Fixed Assets
    fixedAssets: 'Fixed Assets',
    fixedAssetCategories: 'Asset Categories',
    fixedAssetsList: 'Fixed Assets',
    depreciationRun: 'Depreciation Run',
    fixedAssetsReport: 'Asset Register',
    // Accounting Reports
    accountingReports: 'Accounting Reports',
    trialBalance: 'Trial Balance',
    generalLedger: 'General Ledger',
    financialStatements: 'Financial Statements',
    operationsMonitor: 'Operations Monitor',
    accountStatement: 'Account Statement',
    branchProfitLoss: 'Branch Profit & Loss',
    custodyStatement: 'Custody Statement',
    // Document Archive
    documentArchive: 'Document Archive',
    transactionLog: 'Transaction Log',
    documentAttachments: 'Document Attachments',
    // Tools
    toolsUtilities: 'Tools',
    importExport: 'Import/Export',
    // Development Tools
    devTools: 'Dev Tools',
    testAccountSelector: 'Test Account Selector',
    testSelectors: 'Test Selectors',
    // Settings
    settings: 'Settings',
    logout: 'Logout',
    // Tooltips
    openMenu: 'Open Menu',
    closeMenu: 'Close Menu',
    switchLanguage: 'العربية',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
  },
  ar: {
    systemName: 'كونتورا',
    // Dashboard
    dashboard: 'لوحة التحكم',
    // Administration
    administration: 'الإدارة',
    users: 'المستخدمين',
    roles: 'الأدوار والصلاحيات',
    company: 'بيانات الشركة',
    // Master Data
    masterData: 'البيانات الأساسية',
    items: 'الأصناف',
    categories: 'التصنيفات',
    units: 'وحدات القياس',
    branches: 'الفروع',
    warehouses: 'المخازن',
    // Partners
    partners: 'الشركاء',
    suppliers: 'الموردين',
    customers: 'العملاء',
    // HR & Custody
    hrCustody: 'الموارد البشرية والعهد',
    employees: 'الموظفين',
    custodies: 'العهد النقدية',
    employeeCustodySummary: 'ملخص عُهد الموظف',
    custodyExpenseCategories: 'فئات المصاريف',
    custodySettlements: 'كشوف تسوية العُهد',
    // HR & Payroll
    hrPayroll: 'الموارد البشرية والرواتب',
    timeSheet: 'سجل الحضور',
    timeSheetReport: 'تقرير سجل الحضور',
    dailyAttendance: 'الحضور اليومي',
    leaves: 'الإجازات',
    payrolls: 'الرواتب',
    payrollReport: 'تقرير الرواتب',
    hrAudit: 'تقرير الرقابة والتدقيق',
    absences: 'الغياب',
    deductions: 'الخصومات',
    warnings: 'الإنذارات',
    // Purchases
    purchases: 'المشتريات',
    purchaseOrders: 'أوامر الشراء',
    purchaseInvoices: 'فواتير الشراء',
    purchaseReturns: 'مرتجعات الشراء',
    purchaseConsolidation: 'تجميع الطلبات',
    bulkPricing: 'التسعير الجماعي',
    bakeryPurchases: 'مشتريات المخبز',
    // Stock Operations
    stockOperations: 'عمليات المخزون',
    inventoryOpeningBalances: 'أرصدة افتتاحية',
    stockIssues: 'إذونات الصرف',
    warehouseTransfers: 'تحويل بين المخازن',
    branchTransfers: 'تحويل بين الفروع',
    stockCounts: 'جرد المخزون',
    branchIssueRequests: 'طلبات الإصدار من المخبز',
    branchIssues: 'إصدارات المخبز للفروع',
    stockLevels: 'أرصدة المخزون',
    stockMovements: 'حركات المخزون',
    // Accounting
    accounting: 'المحاسبة',
    chartOfAccounts: 'دليل الحسابات',
    costCenters: 'مراكز التكلفة',
    entryTypes: 'أنواع القيود',
    fiscalPeriods: 'الفترات المالية',
    openingEntries: 'القيود الافتتاحية',
    journalEntries: 'قيود اليومية',
    batchPosting: 'الترحيل الجماعي',
    // Vouchers
    vouchers: 'السندات',
    cashReceipts: 'سند قبض نقدي',
    cashPayments: 'سند صرف نقدي',
    checkReceipts: 'سند قبض بشيك',
    checkPayments: 'سند صرف بشيك',
    // Daily Sales System
    dailySalesSystem: 'المبيعات اليومية',
    concepts: 'المفاهيم',
    paymentMethods: 'طرق الدفع',
    accountConfigurations: 'إعدادات الحسابات',
    dailySales: 'المبيعات اليومية',
    // Inventory Reports
    inventoryReports: 'تقارير المخزون',
    stockLevelsReport: 'أرصدة المخزون',
    stockMovementsReport: 'حركات المخزون',
    stockIssuesReport: 'إذونات الصرف',
    warehouseTransfersReport: 'تحويلات المخازن',
    branchTransfersReport: 'تحويلات الفروع',
    stockCountsReport: 'تقارير الجرد',
    expiryReport: 'تقرير الصلاحيات',
    fastMovingReport: 'أصناف سريعة الحركة',
    slowMovingReport: 'أصناف بطيئة الحركة',
    inventoryValuationReport: 'تقييم المخزون',
    itemMovementStatementReport: 'كشف حساب الصنف',
    // Purchase Reports
    purchaseReports: 'تقارير المشتريات',
    purchaseOrdersReport: 'أوامر الشراء',
    purchaseInvoicesReport: 'فواتير الشراء',
    purchaseReturnsReport: 'مرتجعات الشراء',
    consolidationReport: 'التوحيد',
    // Fixed Assets
    fixedAssets: 'الأصول الثابتة',
    fixedAssetCategories: 'تصنيفات الأصول',
    fixedAssetsList: 'الأصول الثابتة',
    depreciationRun: 'تشغيل الإهلاك',
    fixedAssetsReport: 'سجل الأصول',
    // Accounting Reports
    accountingReports: 'التقارير المالية',
    trialBalance: 'ميزان المراجعة',
    generalLedger: 'دفتر الأستاذ',
    financialStatements: 'القوائم المالية',
    operationsMonitor: 'مراقبة العمليات',
    accountStatement: 'كشف حساب',
    branchProfitLoss: 'أرباح وخسائر الفروع',
    custodyStatement: 'كشف حساب العهدة',
    // Document Archive
    documentArchive: 'أرشيف المستندات',
    transactionLog: 'سجل المعاملات',
    documentAttachments: 'مرفقات المستندات',
    // Tools
    toolsUtilities: 'الأدوات',
    importExport: 'استيراد/تصدير',
    // Development Tools
    devTools: 'أدوات التطوير',
    testAccountSelector: 'اختبار محدد الحسابات',
    testSelectors: 'اختبار المحددات',
    // Settings
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    // Tooltips
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    switchLanguage: 'English',
    lightMode: 'الوضع الفاتح',
    darkMode: 'الوضع الداكن',
  },
};

const drawerWidth = 260;

const DashboardLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState<Record<string, boolean>>({
    masterData: true, partners: false, hrCustody: false, hrPayroll: false,
    purchases: false, stockOps: false, accounting: false, vouchers: false,
    dailySales: false, inventoryReports: false, purchaseReports: false,
    accountingReports: false, administration: false, documentArchive: false,
    tools: false, devTools: false, fixedAssets: false,
  });
  const toggleSection = useCallback((key: string) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const { language, toggleLanguage } = useLanguage();
  const { mode, toggleTheme } = useTheme();
  const { openChat, isOpen, unreadTotal } = useChat();
  const t = translations[language];
  const isRTL = language === 'ar';

  // =============================================
  // 🔐 Permission Check - فحص الصلاحيات
  // =============================================

  // ✅ كشف أمين المخزن من JWT (memoized)
  const isWarehouseKeeper = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const keeperClaims = payload.KeeperWarehouseId;
      if (Array.isArray(keeperClaims)) return keeperClaims.length > 0;
      return !!keeperClaims;
    } catch { return false; }
  }, [user]);

  const isBranchManager = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !!payload.BranchId;
    } catch { return false; }
  }, [user]);

  // ✅ دالة فحص الصلاحية - تعتمد على الصلاحيات التي يمنحها SuperAdmin
  const hasPermission = (requiredPermissions: string[]): boolean => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    // SuperAdmin لديه كل الصلاحيات تلقائياً
    if (user?.isSuperAdmin) return true;
    // باقي المستخدمين يعتمدون على الصلاحيات الممنوحة لهم
    return requiredPermissions.some(p => permissions.includes(p));
  };

  const handleLogout = async () => {
    setLoggingOut(true); // منع كل الطلبات المعلقة من إنتاج أخطاء
    await authService.logout(); // notify server (set IsOnline=false, LastLogoutDate)
    dispatch(logout());
    navigate('/login');
    setTimeout(() => setLoggingOut(false), 500); // إعادة التفعيل بعد الانتقال
  };

  const isActive = (path: string) => location.pathname === path;

  // ═══════════════════════════════════════════════════════════════
  // 📋 MENU STRUCTURE — memoized to avoid recreating 80+ items per render
  // ═══════════════════════════════════════════════════════════════
  const menuGroups = useMemo(() => [
    // ─────────────────────────────────────────
    // 🏠 Dashboard - لوحة التحكم
    // ─────────────────────────────────────────
    {
      title: null,
      items: [
        { text: t.dashboard, icon: <DashboardIcon />, path: '/dashboard', permissions: ['DASHBOARD_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 📦 Master Data - البيانات الأساسية
    // ─────────────────────────────────────────
    {
      title: t.masterData,
      collapsible: true,
      sectionKey: 'masterData',
      open: sections.masterData,
      items: [
        { text: t.items, icon: <InventoryIcon />, path: '/items', permissions: ['ITEMS_VIEW'] },
        { text: t.categories, icon: <CategoryIcon />, path: '/categories', permissions: ['CATEGORY_VIEW'] },
        { text: t.units, icon: <UnitIcon />, path: '/units', permissions: ['UNIT_VIEW'] },
        { text: t.warehouses, icon: <WarehouseIcon />, path: '/warehouses', permissions: ['WAREHOUSE_VIEW'] },
        { text: t.branches, icon: <BranchIcon />, path: '/branches', permissions: ['BRANCH_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 🤝 Partners - الشركاء (موردين + عملاء)
    // ─────────────────────────────────────────
    {
      title: t.partners,
      collapsible: true,
      sectionKey: 'partners',
      open: sections.partners,
      items: [
        { text: t.suppliers, icon: <SupplierIcon />, path: '/suppliers', permissions: ['SUPPLIER_VIEW'] },
        { text: t.customers, icon: <CustomersIcon />, path: '/customers', permissions: ['CUSTOMER_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 👥 HR & Custody - الموارد البشرية والعهد
    // ✅ تم فصل صلاحيات فئات المصاريف وكشوف التسوية
    // ─────────────────────────────────────────
    {
      title: t.hrCustody,
      collapsible: true,
      sectionKey: 'hrCustody',
      open: sections.hrCustody,
      items: [
        { text: t.employees, icon: <EmployeeIcon />, path: '/employees', permissions: ['EMPLOYEES_VIEW'] },
        { text: t.custodies, icon: <CustodyIcon />, path: '/custodies', permissions: ['CUSTODY_VIEW'] },
        { text: t.employeeCustodySummary, icon: <CustodyIcon />, path: '/employee-custody-summary', permissions: ['CUSTODY_SUMMARY_VIEW', 'CUSTODY_VIEW'] },
        { text: t.custodyStatement, icon: <ReportDocIcon />, path: '/reports/custody-statement', permissions: ['CUSTODY_STATEMENT_VIEW', 'CUSTODY_VIEW'] },
        // ✅ فئات المصاريف: تظهر لمن لديه CUSTODY_EXPENSE_CAT_VIEW أو CUSTODY_VIEW
        { text: t.custodyExpenseCategories, icon: <ConfigIcon />, path: '/custody-expense-categories', permissions: ['CUSTODY_EXPENSE_CAT_VIEW', 'CUSTODY_VIEW'] },
        // ✅ كشوف التسوية: تظهر لمن لديه CUSTODY_SETTLEMENT_VIEW أو CUSTODY_VIEW
        { text: t.custodySettlements, icon: <ReceiptIcon />, path: '/custody-settlements', permissions: ['CUSTODY_SETTLEMENT_VIEW', 'CUSTODY_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 💼 HR & Payroll - الموارد البشرية والرواتب
    // ─────────────────────────────────────────
    {
      title: t.hrPayroll,
      collapsible: true,
      sectionKey: 'hrPayroll',
      open: sections.hrPayroll,
      items: [
        { text: t.timeSheet, icon: <TimeSheetIcon />, path: '/timesheet', permissions: ['TIMESHEET_VIEW'] },
        { text: t.timeSheetReport, icon: <ReportDocIcon />, path: '/timesheet-report', permissions: ['TIMESHEET_VIEW'] },
        { text: t.leaves, icon: <LeaveIcon />, path: '/leaves', permissions: ['LEAVE_VIEW'] },
        { text: t.payrolls, icon: <PayrollIcon />, path: '/payrolls', permissions: ['PAYROLL_VIEW'] },
        { text: t.payrollReport, icon: <ReportDocIcon />, path: '/payroll-report', permissions: ['PAYROLL_VIEW'] },
        { text: t.hrAudit, icon: <ReportDocIcon />, path: '/hr-audit', permissions: ['HR_AUDIT_VIEW'] },
        { text: t.absences, icon: <AbsenceIcon />, path: '/absences', permissions: ['ABSENCE_VIEW'] },
        { text: t.deductions, icon: <DeductionIcon />, path: '/deductions', permissions: ['DEDUCTION_VIEW'] },
        { text: t.warnings, icon: <WarningIcon />, path: '/warnings', permissions: ['WARNING_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 🛒 Purchases - المشتريات
    // ─────────────────────────────────────────
    {
      title: t.purchases,
      collapsible: true,
      sectionKey: 'purchases',
      open: sections.purchases,
      items: [
        { text: t.purchaseOrders, icon: <PurchaseIcon />, path: '/purchase-orders', permissions: ['PURCHASES_VIEW'] },
        { text: t.purchaseInvoices, icon: <ReceiptIcon />, path: '/purchase-invoices', permissions: ['PURCHASES_VIEW'] },
        { text: t.purchaseReturns, icon: <ReceiptIcon />, path: '/purchase-returns', permissions: ['PURCHASE_RETURN_VIEW'] },
        { text: t.purchaseConsolidation, icon: <ShippingIcon />, path: '/purchase-consolidation', permissions: ['PURCHASE_CONSOLIDATION_VIEW'] },
        { text: t.bulkPricing, icon: <PriceIcon />, path: '/bulk-pricing', permissions: ['PURCHASE_BULK_PRICING'] },
        { text: t.bakeryPurchases, icon: <BakeryIssueIcon />, path: '/bakery-purchases', permissions: ['PURCHASES_VIEW'], warehouseOnly: true },
      ]
    },

    // ─────────────────────────────────────────
    // 📊 Stock Operations - عمليات المخزون
    // ─────────────────────────────────────────
    {
      title: t.stockOperations,
      collapsible: true,
      sectionKey: 'stockOps',
      open: sections.stockOps,
      items: [
        { text: t.inventoryOpeningBalances, icon: <OpeningIcon />, path: '/inventory-opening-balances', permissions: ['INVENTORY_OPENING'] },
        { text: t.stockIssues, icon: <StockMoveIcon />, path: '/stock-issues', permissions: ['STOCK_ISSUE_CREATE', 'INVENTORY_ADJUST'] },
        { text: isRTL ? 'الصرف الداخلي' : 'Internal Issues', icon: <StockMoveIcon />, path: '/internal-issues', permissions: ['INTERNAL_ISSUE_VIEW'] },
        { text: t.warehouseTransfers, icon: <TransferIcon />, path: '/warehouse-transfers', permissions: ['INVENTORY_TRANSFER', 'WAREHOUSE_VIEW'] },
        { text: t.branchTransfers, icon: <TransferIcon />, path: '/branch-transfers', permissions: ['INVENTORY_TRANSFER'], hideForKeeper: true },
        { text: t.stockCounts, icon: <StockCountIcon />, path: '/stock-counts', permissions: ['STOCK_ADJUSTMENT_CREATE', 'INVENTORY_ADJUST'] },
        { text: t.branchIssueRequests, icon: <BakeryIssueIcon />, path: '/branch-issue-requests', permissions: ['BRANCH_ISSUE_REQUEST_VIEW', 'BRANCH_ISSUE_REQUEST_CREATE'] },
        { text: isRTL ? 'طلبات الشراء المركزية' : 'Central Purchase Requests', icon: <BakeryIssueIcon />, path: '/branch-purchase-requests', permissions: ['BRANCH_PURCHASE_REQUEST_VIEW', 'BRANCH_PURCHASE_REQUEST_CREATE'] },
        { text: t.branchIssues, icon: <BakeryIssueIcon />, path: '/branch-issues', permissions: ['BRANCH_ISSUE_VIEW', 'BRANCH_ISSUE_CREATE'] },
        { text: t.stockLevels, icon: <InventoryIcon />, path: '/stock-levels', permissions: ['STOCK_VIEW', 'INVENTORY_VIEW'] },
        { text: t.stockMovements, icon: <TransferIcon />, path: '/stock-movements', permissions: ['REPORT_STOCK_MOVEMENT'] },
      ]
    },

    // ─────────────────────────────────────────
    // 📒 Accounting - المحاسبة
    // ─────────────────────────────────────────
    {
      title: t.accounting,
      collapsible: true,
      sectionKey: 'accounting',
      open: sections.accounting,
      items: [
        { text: t.chartOfAccounts, icon: <AccountTreeIcon />, path: '/chart-of-accounts', permissions: ['CHART_OF_ACCOUNTS_VIEW'] },
        { text: t.costCenters, icon: <AccountingIcon />, path: '/cost-centers', permissions: ['ACCOUNTING_VIEW'] },
        { text: t.entryTypes, icon: <EntryTypeIcon />, path: '/entry-types', permissions: ['ACCOUNTING_VIEW'] },
        { text: t.fiscalPeriods, icon: <CalendarIcon />, path: '/fiscal-periods', permissions: ['FISCAL_PERIOD_VIEW'] },
        { text: t.openingEntries, icon: <OpeningIcon />, path: '/opening-entries', permissions: ['OPENING_ENTRIES'] },
        { text: t.journalEntries, icon: <ReceiptIcon />, path: '/journal-entries', permissions: ['JOURNAL_VIEW'] },
        { text: t.batchPosting, icon: <BatchPostIcon />, path: '/batch-posting', permissions: ['BATCH_POST'] },
      ]
    },

    // ─────────────────────────────────────────
    // 💳 Vouchers - السندات
    // ─────────────────────────────────────────
    {
      title: t.vouchers,
      collapsible: true,
      sectionKey: 'vouchers',
      open: sections.vouchers,
      items: [
        { text: t.cashReceipts, icon: <PaymentIcon />, path: '/accounting/cash-receipts', permissions: ['RECEIPT_VIEW'] },
        { text: t.cashPayments, icon: <PaymentIcon />, path: '/accounting/cash-payments', permissions: ['PAYMENT_VIEW'] },
        { text: t.checkReceipts, icon: <VoucherIcon />, path: '/accounting/check-receipts', permissions: ['CHECK_MANAGE'] },
        { text: t.checkPayments, icon: <VoucherIcon />, path: '/accounting/check-payments', permissions: ['CHECK_MANAGE'] },
        // V2 sandbox — visible only to Super Admins (hasPermission() returns
        // true for SA on any permission key, so we use a synthetic key that
        // nobody is granted; the SA bypass in hasPermission() lets them in).
        ...(user?.isSuperAdmin ? [
          { text: (isRTL ? '🧪 Smart Entry V2' : '🧪 Smart Entry V2'), icon: <PaymentIcon />, path: '/cash-payments-v2', permissions: ['__SA_ONLY__'] as string[] },
        ] : []),
      ]
    },

    // ─────────────────────────────────────────
    // 🛍️ Daily Sales System - نظام المبيعات اليومية
    // ─────────────────────────────────────────
    {
      title: t.dailySalesSystem,
      collapsible: true,
      sectionKey: 'dailySales',
      open: sections.dailySales,
      items: [
        { text: t.concepts, icon: <ConceptIcon />, path: '/concepts', permissions: ['CONCEPT_VIEW'] },
        { text: t.paymentMethods, icon: <PaymentMethodIcon />, path: '/payment-methods', permissions: ['PAYMENT_METHOD_VIEW'] },
        { text: t.accountConfigurations, icon: <ConfigIcon />, path: '/account-configurations', permissions: ['DAILY_SALES_CONFIG_VIEW'] },
        { text: t.dailySales, icon: <DailySalesIcon />, path: '/daily-sales', permissions: ['DAILY_SALES_VIEW'] },
        { text: isRTL ? 'ملخص المبيعات (شجري)' : 'Sales Summary (Tree)', icon: <BarChart />, path: '/reports/daily-sales-summary', permissions: ['REPORT_DAILY_SALES_SUMMARY'] },
      ]
    },

    // ─────────────────────────────────────────
    // 📈 Inventory Reports - تقارير المخزون
    // ─────────────────────────────────────────
    {
      title: t.inventoryReports,
      collapsible: true,
      sectionKey: 'inventoryReports',
      open: sections.inventoryReports,
      items: [
        { text: t.stockLevelsReport, icon: <InventoryIcon />, path: '/reports/stock-levels', permissions: ['REPORT_INVENTORY'] },
        { text: t.stockMovementsReport, icon: <TransferIcon />, path: '/reports/stock-movements', permissions: ['REPORT_STOCK_MOVEMENT'] },
        { text: t.fastMovingReport, icon: <FastForward />, path: '/reports/fast-moving', permissions: ['REPORT_INVENTORY'] },
        { text: t.slowMovingReport, icon: <TrendingDown />, path: '/reports/slow-moving', permissions: ['REPORT_INVENTORY'] },
        { text: t.expiryReport, icon: <Schedule />, path: '/reports/expiry', permissions: ['REPORT_INVENTORY'] },
        { text: t.inventoryValuationReport, icon: <Insights />, path: '/reports/inventory-valuation', permissions: ['REPORT_INVENTORY'] },
        { text: isRTL ? 'المخزون الفعلي' : 'Actual Stock', icon: <BarChart />, path: '/reports/actual-stock', permissions: ['REPORT_ACTUAL_STOCK'] },
        { text: isRTL ? 'صرفيات المخزن للفروع' : 'Warehouse Dispatch to Branches', icon: <TransferIcon />, path: '/reports/warehouse-dispatch', permissions: ['REPORT_WAREHOUSE_DISPATCH'] },
        { text: isRTL ? 'قصة حياة الصنف' : 'Item Lifecycle Story', icon: <InventoryIcon />, path: '/reports/item-lifecycle', permissions: ['REPORT_ITEM_LIFECYCLE'] },
        { text: isRTL ? 'وارد الفرع' : 'Branch Receipts', icon: <TransferIcon />, path: '/reports/branch-receipts', permissions: ['REPORT_BRANCH_RECEIPTS'] },
        { text: isRTL ? 'الأصناف ووحدات القياس' : 'Items & Units of Measure', icon: <BarChart />, path: '/reports/items-units', permissions: ['REPORT_ITEMS_UNITS'] },
        { text: t.itemMovementStatementReport, icon: <ReportsIcon />, path: '/reports/item-movement-statement', permissions: ['REPORT_ITEM_MOVEMENT_STATEMENT'] },
      ]
    },

    // ─────────────────────────────────────────
    // 🛒 Purchase Reports - تقارير المشتريات
    // ─────────────────────────────────────────
    {
      title: t.purchaseReports,
      collapsible: true,
      sectionKey: 'purchaseReports',
      open: sections.purchaseReports,
      items: [
        { text: t.purchaseOrdersReport, icon: <PurchaseIcon />, path: '/reports/purchase-orders', permissions: ['REPORT_PURCHASES'] },
        { text: t.purchaseInvoicesReport, icon: <ReceiptIcon />, path: '/reports/purchase-invoices', permissions: ['REPORT_PURCHASES'] },
        { text: t.purchaseReturnsReport, icon: <ReceiptIcon />, path: '/reports/purchase-returns', permissions: ['REPORT_PURCHASES'] },
        { text: t.consolidationReport, icon: <ReportsIcon />, path: '/reports/consolidation', permissions: ['REPORT_PURCHASES'] },
        { text: t.warehouseTransfersReport, icon: <TransferIcon />, path: '/reports/warehouse-transfers', permissions: ['WAREHOUSE_VIEW'] },
        { text: t.branchTransfersReport, icon: <TransferIcon />, path: '/reports/branch-transfers', permissions: ['REPORT_INVENTORY'], hideForKeeper: true },
        { text: t.stockCountsReport, icon: <StockCountIcon />, path: '/reports/stock-counts', permissions: ['REPORT_INVENTORY'] },
        { text: t.stockIssuesReport, icon: <StockMoveIcon />, path: '/reports/stock-issues', permissions: ['REPORT_INVENTORY'] },
        { text: isRTL ? 'دورة طلبات الشراء الفرعية' : 'BPR Workflow', icon: <ReportsIcon />, path: '/reports/bpr-workflow', permissions: ['REPORT_BPR_WORKFLOW'] },
        { text: isRTL ? 'تقرير مشتريات العُهد' : 'Custody Purchases Report', icon: <ReportsIcon />, path: '/custody-purchases-report', permissions: ['CUSTODY_PURCHASES_REPORT_VIEW', 'CUSTODY_PURCHASES_REPORT_VIEW_ALL'] },
      ]
    },

    // ─────────────────────────────────────────
    // 📊 Accounting Reports - التقارير المالية
    // ─────────────────────────────────────────
    {
      title: t.accountingReports,
      collapsible: true,
      sectionKey: 'accountingReports',
      open: sections.accountingReports,
      items: [
        { text: t.trialBalance, icon: <TrialBalanceIcon />, path: '/reports/trial-balance', permissions: ['REPORT_TRIAL_BALANCE'] },
        { text: t.generalLedger, icon: <LedgerIcon />, path: '/reports/general-ledger', permissions: ['REPORT_LEDGER'] },
        { text: t.financialStatements, icon: <FinancialReportIcon />, path: '/reports/financial-statements', permissions: ['REPORT_FINANCIAL'] },
        { text: t.accountStatement, icon: <ReportsIcon />, path: '/reports/account-statement', permissions: ['REPORT_LEDGER'] },
        { text: t.branchProfitLoss, icon: <BarChart />, path: '/reports/branch-profit-loss', permissions: ['REPORT_BRANCH_PROFIT_LOSS'] },
        { text: t.operationsMonitor || 'Operations Monitor', icon: <BarChart />, path: '/reports/operations-monitor', permissions: ['REPORT_OPERATIONS_MONITOR'] },
        { text: isRTL ? 'سلامة البيانات' : 'Data Integrity', icon: <BarChart />, path: '/reports/data-integrity', permissions: ['REPORT_DATA_INTEGRITY'] },
        { text: isRTL ? 'التحليل المالي' : 'Financial Analysis', icon: <BarChart />, path: '/reports/financial-analysis', permissions: ['FINANCIAL_ANALYSIS'] },
        { text: isRTL ? 'التحقق من التكلفة' : 'Cost Verification', icon: <BarChart />, path: '/reports/cost-verification', permissions: ['REPORT_COST_VERIFICATION'] },
        { text: isRTL ? 'إعادة ضبط المخزون' : 'Inventory Rebalance', icon: <BarChart />, path: '/tools/inventory-rebalance', permissions: ['INVENTORY_REBALANCE'] },
        { text: isRTL ? '🏥 مركز صحة المخزون (السوبر أدمن)' : '🏥 Inventory Health Hub (Super Admin)', icon: <BarChart />, path: '/admin/inventory-health-hub', permissions: ['INVENTORY_HEALTH_HUB'] },
        { text: isRTL ? '🧠 المحلل الخبير للأصناف (السوبر أدمن)' : '🧠 Smart Item Auditor (Super Admin)', icon: <BarChart />, path: '/admin/item-audit', permissions: ['ITEM_AUDIT_VIEW'] },
        { text: isRTL ? 'سجل التدقيق' : 'Audit Trail', icon: <BarChart />, path: '/admin/audit-log', permissions: ['DATABASE_ADMIN'] },
      ]
    },

    // ─────────────────────────────────────────
    // 🏢 Fixed Assets - الأصول الثابتة
    // ─────────────────────────────────────────
    {
      title: t.fixedAssets,
      collapsible: true,
      sectionKey: 'fixedAssets',
      open: sections.fixedAssets,
      items: [
        { text: t.fixedAssetCategories, icon: <CategoryIcon />, path: '/fixed-asset-categories', permissions: ['ACCOUNTING_VIEW'] },
        { text: t.fixedAssetsList, icon: <AccountingIcon />, path: '/fixed-assets', permissions: ['ACCOUNTING_VIEW'] },
        { text: t.depreciationRun, icon: <BatchPostIcon />, path: '/depreciation-run', permissions: ['ACCOUNTING_VIEW'] },
        { text: t.fixedAssetsReport, icon: <FinancialReportIcon />, path: '/reports/fixed-assets', permissions: ['ACCOUNTING_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // 📎 Document Archive - أرشيف المستندات
    // ─────────────────────────────────────────
    {
      title: t.documentArchive,
      collapsible: true,
      sectionKey: 'documentArchive',
      open: sections.documentArchive,
      items: [
        { text: t.transactionLog, icon: <ArchiveIcon />, path: '/transaction-log', permissions: ['ACCOUNTING_VIEW', 'PURCHASE_VIEW', 'INVENTORY_VIEW'] },
        { text: t.documentAttachments, icon: <AttachIcon />, path: '/document-attachments', permissions: [] },
      ]
    },

    // ─────────────────────────────────────────
    // 🔧 Tools - الأدوات
    // ─────────────────────────────────────────
    {
      title: t.toolsUtilities,
      collapsible: true,
      sectionKey: 'tools',
      open: sections.tools,
      items: [
        { text: t.importExport, icon: <ImportExportIcon />, path: '/chart-of-accounts/import-export', permissions: ['CHART_OF_ACCOUNTS_MANAGE'] },
      ]
    },

    // ─────────────────────────────────────────
    // 🐛 Development Tools - أدوات التطوير
    // ─────────────────────────────────────────
    {
      title: t.devTools,
      collapsible: true,
      sectionKey: 'devTools',
      open: sections.devTools,
      items: [
        { text: t.testAccountSelector, icon: <TestIcon />, path: '/test-account-selector', permissions: [] },
        { text: t.testSelectors, icon: <DevIcon />, path: '/test-selectors', permissions: [] },
      ]
    },

    // ─────────────────────────────────────────
    // 👤 Administration - الإدارة
    // ─────────────────────────────────────────
    {
      title: t.administration,
      collapsible: true,
      sectionKey: 'administration',
      open: sections.administration,
      items: [
        // المستخدمين - يعتمد على صلاحية USERS_VIEW
        { text: t.users, icon: <UsersIcon />, path: '/settings/users', permissions: ['USERS_VIEW'] },
        // الأدوار - يعتمد على صلاحية ROLES_VIEW
        { text: t.roles, icon: <RolesIcon />, path: '/settings/roles', permissions: ['ROLES_VIEW'] },
        // بيانات الشركة - يعتمد على صلاحية SETTINGS_VIEW
        { text: t.company, icon: <CompanyIcon />, path: '/settings', permissions: ['SETTINGS_VIEW'] },
      ]
    },

    // ─────────────────────────────────────────
    // ⚙️ Settings - الإعدادات
    // ─────────────────────────────────────────
    {
      title: null,
      items: [
        { text: t.settings, icon: <SettingsIcon />, path: '/settings', permissions: ['SETTINGS_VIEW'] },
      ]
    },
  ], [t, sections]);

  // ✅ فلترة القائمة حسب الصلاحيات + إخفاء عناصر أمين المخزن (memoized)
  const filteredMenuGroups = useMemo(() => menuGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (!hasPermission(item.permissions)) return false;
        if ((item as any).hideForKeeper && isWarehouseKeeper) return false;
        if ((item as any).warehouseOnly && isBranchManager && !user?.isSuperAdmin) return false;
        return true;
      })
    }))
    .filter(group => group.items.length > 0),
  [menuGroups, permissions, user?.isSuperAdmin, isWarehouseKeeper, isBranchManager]);

  return (
    <Box sx={{ display: 'flex', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 🔝 APP BAR */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1, 
          background: mode === 'dark' 
            ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 50%, #3d4a6c 100%)' 
            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(79,172,254,0.3)'
        }}
      >
        <Toolbar>
          <Tooltip title={open ? t.closeMenu : t.openMenu}>
            <IconButton color="inherit" edge="start" onClick={() => setOpen(!open)} sx={{ mr: isRTL ? 0 : 2, ml: isRTL ? 2 : 0 }}>
              {open ? (isRTL ? <ChevronRight /> : <ChevronLeft />) : <MenuIcon />}
            </IconButton>
          </Tooltip>
          
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', mr: 2, letterSpacing: 1 }}>
            {t.systemName}
          </Typography>
          
          <FiscalYearSelector variant="header" />
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t.switchLanguage}>
              <IconButton onClick={toggleLanguage} sx={{ color: 'white' }}>
                <Language />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={mode === 'dark' ? t.lightMode : t.darkMode}>
              <IconButton onClick={toggleTheme} sx={{ color: 'white' }}>
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Messages">
              <IconButton onClick={openChat} sx={{ color: 'white' }}>
                <Badge badgeContent={unreadTotal > 0 ? unreadTotal : undefined} color="error" max={99}>
                  <ChatIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <NotificationBell />

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 1 }}>
              <Avatar 
                sx={{ 
                  bgcolor: mode === 'dark' ? 'rgba(79,172,254,0.3)' : 'rgba(255,255,255,0.3)', 
                  width: 38, 
                  height: 38,
                  border: '2px solid rgba(255,255,255,0.4)',
                  fontWeight: 600
                }}
              >
                {user?.userName?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: isRTL ? 'flex-end' : 'flex-start' }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
                  {user?.fullName || user?.userName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                  {user?.roles?.[0] || 'User'}{user?.branchName ? ` — ${user.branchName}` : ''}
                </Typography>
              </Box>
            </Box>
            
            <Tooltip title={t.logout}>
              <IconButton onClick={handleLogout} sx={{ color: 'white' }}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📋 SIDEBAR DRAWER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Drawer 
        variant="persistent" 
        anchor={isRTL ? 'right' : 'left'} 
        open={open} 
        sx={{ 
          width: drawerWidth, 
          flexShrink: 0, 
          '& .MuiDrawer-paper': { 
            width: drawerWidth, 
            boxSizing: 'border-box', 
            background: mode === 'dark' 
              ? 'linear-gradient(180deg, #1e2a4a 0%, #1a2340 50%, #151c32 100%)' 
              : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            borderRight: isRTL ? 'none' : `1px solid ${mode === 'dark' ? 'rgba(79,172,254,0.15)' : 'rgba(0,0,0,0.08)'}`,
            borderLeft: isRTL ? `1px solid ${mode === 'dark' ? 'rgba(79,172,254,0.15)' : 'rgba(0,0,0,0.08)'}` : 'none',
            boxShadow: mode === 'dark' ? '4px 0 20px rgba(0,0,0,0.3)' : '4px 0 20px rgba(0,0,0,0.05)',
          } 
        }}
      >
        <Toolbar />
        <Box sx={{ 
          overflow: 'auto', 
          mt: 1, 
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'dark' 
              ? 'linear-gradient(180deg, #4facfe 0%, #00f2fe 100%)'
              : 'linear-gradient(180deg, #0284c7 0%, #0ea5e9 100%)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: mode === 'dark'
              ? 'linear-gradient(180deg, #00f2fe 0%, #4facfe 100%)'
              : 'linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%)',
          },
        }}>
          <List>
            {filteredMenuGroups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.title && (
                  <>
                    {groupIndex > 0 && <Divider sx={{ my: 1, mx: 2, borderColor: mode === 'dark' ? 'rgba(79,172,254,0.15)' : 'rgba(0,0,0,0.06)' }} />}
                    {group.collapsible ? (
                      <ListItemButton
                        onClick={() => group.sectionKey && toggleSection(group.sectionKey)}
                        sx={{ 
                          px: 2, 
                          py: 0.75, 
                          flexDirection: isRTL ? 'row-reverse' : 'row',
                          '&:hover': { background: mode === 'dark' ? 'rgba(79,172,254,0.08)' : 'rgba(79,172,254,0.06)' }
                        }}
                      >
                        <ListSubheader 
                          component="div" 
                          sx={{ 
                            background: 'transparent', 
                            lineHeight: 'normal', 
                            p: 0, 
                            flex: 1, 
                            textAlign: isRTL ? 'right' : 'left', 
                            fontWeight: 700, 
                            fontSize: '0.75rem', 
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            color: mode === 'dark' ? '#4facfe' : '#0284c7' 
                          }}
                        >
                          {group.title}
                        </ListSubheader>
                        {group.open ? <ExpandLess sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7', fontSize: '1.2rem' }} /> : <ExpandMore sx={{ color: mode === 'dark' ? '#4facfe' : '#0284c7', fontSize: '1.2rem' }} />}
                      </ListItemButton>
                    ) : (
                      <ListSubheader 
                        component="div" 
                        sx={{ 
                          background: 'transparent', 
                          px: 2, 
                          py: 1, 
                          fontWeight: 700, 
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                          color: mode === 'dark' ? '#4facfe' : '#0284c7' 
                        }}
                      >
                        {group.title}
                      </ListSubheader>
                    )}
                  </>
                )}
                <Collapse in={group.collapsible ? group.open : true} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.map((item) => (
                      <ListItem key={item.text} disablePadding sx={{ mb: 0.25, px: 1 }}>
                        <ListItemButton
                          component={Link}
                          to={item.path}
                          selected={isActive(item.path)} 
                          sx={{ 
                            borderRadius: 2, 
                            py: 0.75,
                            pl: group.title ? 3 : 2, 
                            flexDirection: isRTL ? 'row-reverse' : 'row', 
                            background: isActive(item.path) 
                              ? mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(79,172,254,0.2) 0%, rgba(0,242,254,0.1) 100%)' 
                                : 'linear-gradient(135deg, rgba(79,172,254,0.15) 0%, rgba(0,242,254,0.1) 100%)' 
                              : 'transparent', 
                            borderLeft: isActive(item.path) && !isRTL ? `3px solid ${mode === 'dark' ? '#4facfe' : '#0284c7'}` : 'none',
                            borderRight: isActive(item.path) && isRTL ? `3px solid ${mode === 'dark' ? '#4facfe' : '#0284c7'}` : 'none',
                            '&:hover': { 
                              background: mode === 'dark' 
                                ? 'rgba(79,172,254,0.1)' 
                                : 'rgba(79,172,254,0.08)' 
                            }, 
                            '&.Mui-selected': { 
                              background: mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(79,172,254,0.2) 0%, rgba(0,242,254,0.1) 100%)' 
                                : 'linear-gradient(135deg, rgba(79,172,254,0.15) 0%, rgba(0,242,254,0.1) 100%)',
                              '&:hover': { 
                                background: mode === 'dark' 
                                  ? 'linear-gradient(135deg, rgba(79,172,254,0.25) 0%, rgba(0,242,254,0.15) 100%)' 
                                  : 'linear-gradient(135deg, rgba(79,172,254,0.2) 0%, rgba(0,242,254,0.15) 100%)' 
                              } 
                            } 
                          }}
                        >
                          <ListItemIcon 
                            sx={{ 
                              color: isActive(item.path) 
                                ? mode === 'dark' ? '#4facfe' : '#0284c7' 
                                : mode === 'dark' ? '#64748b' : '#64748b', 
                              minWidth: isRTL ? 'auto' : 36, 
                              mr: isRTL ? 0 : 1.5, 
                              ml: isRTL ? 1.5 : 0
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.text} 
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem', 
                              fontWeight: isActive(item.path) ? 600 : 400,
                              color: isActive(item.path) 
                                ? mode === 'dark' ? '#ffffff' : '#0f172a'
                                : mode === 'dark' ? '#94a3b8' : '#475569'
                            }} 
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📄 MAIN CONTENT */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, md: 3 }, 
          [isRTL ? 'marginRight' : 'marginLeft']: open ? 0 : `-${drawerWidth}px`, 
          transition: 'margin 0.3s ease-in-out', 
          minHeight: '100vh', 
          direction: isRTL ? 'rtl' : 'ltr',
          background: mode === 'dark' 
            ? '#0f172a' 
            : '#f1f5f9'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 💬 CHAT WIDGET */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <ChatWidget />
    </Box>
  );
};

export default DashboardLayout;